// EmpilementV2.jsx — recette serpentin 28 plis (algo recette_empilements_v2)
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const CLASSE_COLOR = {
  HIGH:   { border: "#E6FF3E", bg: "#1a2800", text: "#E6FF3E" },
  MEDIUM: { border: "#7ab8cc", bg: "#011829", text: "#7ab8cc" },
  LOW:    { border: "#ff6b6b", bg: "#2a0808", text: "#ff9090" },
};

const DEFAULT_MASSE     = "7000";
const DEFAULT_EPAISSEUR = "28";
const MAX_EMPILEMENTS   = 50;
const MAX_PLIS          = 30;
const PN_SLOT_KEYS = ["pn1", "pn2", "pn3", "pn4", "pn5", "pn6", "pn7", "pn8"];

const EMPTY_PLAGES = Object.fromEntries(
  PN_SLOT_KEYS.map((k) => [k, { deb: "", fin: "" }])
);

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
function clampInt(v, min, max) {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isNaN(n) ? min : Math.max(min, Math.min(max, n));
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent ?? "");
}

function parseOptionalFloat(val) {
  if (val === "" || val == null) return null;
  const n = parseFloat(val);
  return Number.isNaN(n) ? null : n;
}

function buildPlagesPayload(plages) {
  const out = {};
  for (const [slot, { deb, fin }] of Object.entries(plages)) {
    const d = String(deb ?? "").trim();
    const f = String(fin ?? "").trim();
    if (!d && !f) continue;
    if (!d || !f) {
      throw new Error(
        `Plage ${slot.toUpperCase()} : renseigner pli début et pli fin, ou laisser vide.`
      );
    }
    const debN = parseInt(d, 10);
    const finN = parseInt(f, 10);
    if (Number.isNaN(debN) || Number.isNaN(finN)) {
      throw new Error(`Plage ${slot.toUpperCase()} : pli début et fin doivent être des nombres entiers.`);
    }
    if (debN > finN) {
      throw new Error(`Plage ${slot.toUpperCase()} : pli début doit être ≤ pli fin.`);
    }
    out[slot] = { deb: debN, fin: finN };
  }
  return Object.keys(out).length ? out : undefined;
}

/* ─────────────────────────────────────────
   HOOK — useCellDrag
───────────────────────────────────────── */
function useCellDrag(setRows) {
  const dragRef = useRef(null);
  const overRef = useRef(null);

  const onDragStart = useCallback((col, rowIdx) => {
    dragRef.current = { col, rowIdx };
  }, []);

  const onDragOver = useCallback((e, col, rowIdx) => {
    e.preventDefault();
    if (dragRef.current?.col !== col) return;
    overRef.current = { col, rowIdx };
  }, []);

  const onDrop = useCallback((col) => {
    const from = dragRef.current;
    const to   = overRef.current;
    dragRef.current = null;
    overRef.current = null;
    if (!from || !to || from.col !== col || from.rowIdx === to.rowIdx) return;
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      [next[from.rowIdx][col], next[to.rowIdx][col]] = [
        next[to.rowIdx][col],
        next[from.rowIdx][col],
      ];
      return next;
    });
  }, [setRows]);

  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    overRef.current = null;
  }, []);

  return { onDragStart, onDragOver, onDrop, onDragEnd };
}

/* ─────────────────────────────────────────
   HOOK — useClickOutside
───────────────────────────────────────── */
function useClickOutside(ref, callback) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) callback();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, callback]);
}

/* ─────────────────────────────────────────
   HOOK — useClock
───────────────────────────────────────── */
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — PnInput (autocomplete)
───────────────────────────────────────── */
function PnInput({ label, value, onChange, placeholder, pnList }) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState(value);
  const ref = useRef(null);

  // Sync external value → local query
  useEffect(() => setQuery(value), [value]);

  const closeDropdown = useCallback(() => setOpen(false), []);
  useClickOutside(ref, closeDropdown);

  const filtered = query.trim().length === 0
    ? pnList.slice(0, 30)
    : pnList
        .filter((pn) => pn.toLowerCase().includes(query.trim().toLowerCase()))
        .slice(0, 30);

  function handleChange(e) {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    setOpen(true);
  }

  function handleSelect(pn) {
    setQuery(pn);
    onChange(pn);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div className="em-input-label">{label}</div>
      <input
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="em-input-field"
        autoComplete="off"
        spellCheck={false}
      />
      {open && filtered.length > 0 && (
        <div className="em-pn-dropdown" role="listbox">
          {filtered.map((pn) => (
            <div
              key={pn}
              role="option"
              aria-selected={pn === value}
              className="em-pn-dropdown-item"
              onMouseDown={() => handleSelect(pn)}
              style={{
                fontWeight:
                  query && pn.toLowerCase().includes(query.toLowerCase()) ? 700 : 400,
              }}
            >
              {pn}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — PnPlageInput (PN + plage plis optionnelle)
───────────────────────────────────────── */
function PnPlageInput({ label, value, onChange, placeholder, pnList, plage, onPlageChange }) {
  return (
    <div className="em-pn-plage">
      <PnInput
        label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        pnList={pnList}
      />
      <div className="em-plage-row">
        <div>
          <div className="em-input-label">Pli début</div>
          <input
            type="text"
            inputMode="numeric"
            value={plage.deb}
            onChange={(e) => onPlageChange({ ...plage, deb: e.target.value.replace(/\D/g, "") })}
            placeholder="Optionnel"
            className="em-input-field em-input-big-num"
          />
        </div>
        <div>
          <div className="em-input-label">Pli fin</div>
          <input
            type="text"
            inputMode="numeric"
            value={plage.fin}
            onChange={(e) => onPlageChange({ ...plage, fin: e.target.value.replace(/\D/g, "") })}
            placeholder="Optionnel"
            className="em-input-field em-input-big-num"
          />
        </div>
      </div>
    
    </div>
  );
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — DensiteTooltip
───────────────────────────────────────── */
function DensiteTooltip({ tooltip }) {
  if (!tooltip) return null;
  const c = CLASSE_COLOR[tooltip.classe] ?? CLASSE_COLOR.MEDIUM;

  return (
    <div
      style={{
        position: "fixed",
        left: tooltip.x,
        top: tooltip.y,
        transform: "translate(-50%, calc(-100% - 8px))",
        background: "#001f2e",
        border: `2px solid ${c.border}`,
        borderRadius: 8,
        padding: "8px 12px",
        zIndex: 9999,
        pointerEvents: "none",
        minWidth: 210,
        boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
      }}
    >
      {/* PN */}
      <div style={{ color: "#7ab8cc", fontSize: 10, fontWeight: 700, marginBottom: 6 }}>
        {tooltip.pli}
      </div>

      {/* Densité + badge classe */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>
          densité = {tooltip.densite?.toFixed(4) ?? "—"}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4,
          background: c.bg, color: c.text, border: `1px solid ${c.border}`,
        }}>
          {tooltip.classe}
        </span>
      </div>

      {/* Plages par classe */}
      <div style={{ marginTop: 6, borderTop: "1px solid #1a4a5a", paddingTop: 6 }}>
        {["HIGH", "MEDIUM", "LOW"].map((cls) => {
          const cc      = CLASSE_COLOR[cls];
          const active  = tooltip.classe === cls;
          return (
            <div
              key={cls}
              style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 10, marginBottom: 2 }}
            >
              <span style={{ color: cc.text, fontWeight: 800, minWidth: 52 }}>{cls}</span>
              <span style={{ color: active ? "#fff" : "#5a8a99", fontWeight: active ? 800 : 500 }}>
                {tooltip.plages?.[cls] ?? "—"}
              </span>
            </div>
          );
        })}
      </div>

      {/* Infos physiques */}
      <div style={{ marginTop: 6, borderTop: "1px solid #1a4a5a", paddingTop: 6, fontSize: 10, color: "#7ab8cc" }}>
        {tooltip.masse     != null && (
          <div>Masse : <strong style={{ color: "#fff" }}>{tooltip.masse} g</strong></div>
        )}
        {tooltip.epaisseur != null && (
          <div>Épaisseur : <strong style={{ color: "#fff" }}>{tooltip.epaisseur} mm</strong></div>
        )}
        {tooltip.humidite  != null && (
          <div>Humidité : <strong style={{ color: "#fff" }}>{tooltip.humidite}</strong></div>
        )}
        {tooltip.largeur   != null && (
          <div>Largeur : <strong style={{ color: "#fff" }}>{tooltip.largeur} mm</strong></div>
        )}
        {tooltip.longueur  != null && (
          <div>Longueur : <strong style={{ color: "#fff" }}>{tooltip.longueur} mm</strong></div>
        )}
      </div>

      {/* Badges contraintes */}
      {(tooltip.isFace || tooltip.isFissure || tooltip.isNoeud) && (
        <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {tooltip.isFace    && <span style={{ color: "#E6FF3E", fontWeight: 800, fontSize: 10 }}>⭐ FACE</span>}
          {tooltip.isFissure && <span style={{ color: "#ff9090", fontWeight: 800, fontSize: 10 }}>⚠ FISSURE</span>}
          {tooltip.isNoeud   && <span style={{ color: "#ffa040", fontWeight: 800, fontSize: 10 }}>🔗 NŒUD</span>}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — StatsTable
───────────────────────────────────────── */
const STAT_MAIN_COLOR = {
  "Densité moy":    "#7ab8cc",
  "Masse (g)":      "#E6FF3E",
  "Épaisseur (mm)": "#E6FF3E",
};

function StatCell({ value }) {
  const val = String(value ?? "");
  if (!val || val === "—") return <td className="em-td em-td-stat">—</td>;

  const isNeg = val.startsWith("-");
  const isPos = val.startsWith("+");
  const color = isNeg ? "#ff9090" : isPos ? "#80ff90" : "#fff";
  return <td className="em-td em-td-stat" style={{ color }}>{val}</td>;
}

function StatsTable({ stats, empCols }) {
  if (!stats?.length) return null;

  const isDelta = (label) =>
    String(label ?? "").startsWith("Δ") || String(label ?? "").startsWith("Écart");

  const mainStats  = stats.filter((r) => !isDelta(r["Position"]));
 const deltaStats = stats.filter((r) =>
  isDelta(r["Position"]) && String(r["Position"] ?? "") !== "Écart max densité"
);
  return (
    <div className="em-stats-wrap">
      <div className="em-stats-label">📊 Statistiques par empilement</div>
      <div className="em-table-wrap">
        <table className="em-table" style={{ fontSize: 11 }}>
          <tbody>
            {mainStats.map((row, i) => {
              const label = String(row["Position"] ?? "");
              const color = STAT_MAIN_COLOR[label] ?? "#fff";
              return (
                <tr key={`main-${i}`}>
                  <td className="em-td em-td-stat em-td-pos" style={{ color, textAlign: "left", paddingLeft: 8, minWidth: 120 }}>
                    {label}
                  </td>
                  {empCols.map((col) => (
                    <td key={col} className="em-td em-td-stat" style={{ color }}>
                      {row[col] ?? "—"}
                    </td>
                  ))}
                </tr>
              );
            })}

            {deltaStats.length > 0 && (
              <tr>
                <td colSpan={empCols.length + 1}
                  style={{ borderTop: "1px solid #1a4a5a", padding: 0, height: 0 }} />
              </tr>
            )}

            {deltaStats.map((row, i) => {
              const label = String(row["Position"] ?? "");
              return (
                <tr key={`delta-${i}`}>
                  <td className="em-td em-td-stat em-td-pos"
                    style={{ color: "#5a8a99", textAlign: "left", paddingLeft: 8 }}>
                    {label}
                  </td>
                  {empCols.map((col) => (
                    <StatCell key={col} value={row[col]} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — RecetteTable
───────────────────────────────────────── */
function RecetteTable({
  empCols,
  recetteRows,
  setRecetteRows,
  densitiesMap,
  dragHandlers,
}) {
  const [dragOverCell,  setDragOverCell]  = useState(null);
  const [dragActiveCol, setDragActiveCol] = useState(null);
  const [tooltip,       setTooltip]       = useState(null);

  const { onDragStart, onDragOver, onDrop, onDragEnd } = dragHandlers;

  // Build tooltip data from densitiesMap
  function buildTooltip(e, pli) {
    const info = pli && densitiesMap[pli];
    if (!info) return;
    const all  = Object.values(densitiesMap);
    const byClass = (cls) => all.filter((d) => d.classe === cls).map((d) => d.densite);
    const range   = (arr) =>
      arr.length ? `${Math.min(...arr).toFixed(4)} — ${Math.max(...arr).toFixed(4)}` : "—";
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      pli,
      densite:   info.densite,
      classe:    info.classe,
      masse:     info.masse,
      epaisseur: info.epaisseur,
      humidite:  info.humidite,
      largeur:   info.largeur,
      longueur:  info.longueur,
      isFace:    info.isFace,
      isFissure: info.isFissure,
      isNoeud:   info.isNoeud,
      plages: {
        LOW:    range(byClass("LOW")),
        MEDIUM: range(byClass("MEDIUM")),
        HIGH:   range(byClass("HIGH")),
      },
    });
  }

  function moveCellInCol(col, rowIdx, direction) {
    const toIdx = rowIdx + direction;
    if (toIdx < 0 || toIdx >= recetteRows.length) return;
    setRecetteRows((prev) => {
      const next = prev.map((r) => ({ ...r }));
      [next[rowIdx][col], next[toIdx][col]] = [next[toIdx][col], next[rowIdx][col]];
      return next;
    });
  }

  function resetColOrder(col) {
    setRecetteRows((prev) => {
      // Re-sort the values in `col` by original Position order
      const sorted = [...prev].sort((a, b) => (a["Position"] ?? 0) - (b["Position"] ?? 0));
      return prev.map((row, i) => ({ ...row, [col]: sorted[i][col] }));
    });
  }

  return (
    <>
      {/* Legend */}
      <div style={{ display: "flex", gap: 10, fontSize: 10, flexWrap: "wrap", marginBottom: 6 }}>
        {["HIGH", "MEDIUM", "LOW"].map((cls) => (
          <div key={cls} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: CLASSE_COLOR[cls].border }} />
            <span style={{ color: CLASSE_COLOR[cls].text }}>{cls}</span>
          </div>
        ))}
        {[
          { icon: "⭐", color: "#E6FF3E",  label: "FACE"    },
          { icon: "⚠",  color: "#ff9090",  label: "FISSURE" },
          { icon: "🔗", color: "#ffa040",  label: "NŒUD"   },
        ].map(({ icon, color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ color }}>{icon}</span>
            <span style={{ color: "#5a8a99" }}>{label}</span>
          </div>
        ))}
      </div>

      <div className="em-reorder-hint">
        <span>↕</span>
        Glissez les <strong>cellules</strong> dans leur colonne pour réordonner — ou utilisez ▲▼
      </div>

      {/* Table */}
      <div className="em-table-wrap">
        <table className="em-table">
          <thead>
            <tr>
              <th className="em-th em-th-pos">Pos.</th>
              {empCols.map((col) => (
                <th
                  key={col}
                  className={["em-th", dragActiveCol === col ? "em-th-active" : ""].join(" ")}
                >
                  <div className="em-th-inner">
                    <span>{col}</span>
                    <button
                      type="button"
                      className="em-btn-col-reset"
                      onClick={() => resetColOrder(col)}
                      title={`Restaurer ordre initial — ${col}`}
                    >
                      ↺
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recetteRows.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? "em-tr-even" : "em-tr-odd"}>
                <td className="em-td em-td-pos">{rowIdx + 1}</td>
                {empCols.map((col) => {
                  const pli     = row[col];
                  const info    = pli ? densitiesMap[pli] : null;
                  const cellKey = `${col}__${rowIdx}`;
                  const isOver  = dragOverCell === cellKey;
                  const color   = CLASSE_COLOR[info?.classe]?.border ?? "#1a4a5a";

                  return (
                    <td
                      key={col}
                      className={[
                        "em-td em-td-cell",
                        dragActiveCol === col ? "em-td-col-active" : "",
                        isOver               ? "em-td-dragover"   : "",
                      ].join(" ")}
                      style={{ borderLeft: `3px solid ${color}` }}
                      draggable
                      onDragStart={() => {
                        setDragActiveCol(col);
                        onDragStart(col, rowIdx);
                      }}
                      onDragOver={(e) => {
                        onDragOver(e, col, rowIdx);
                        setDragOverCell(cellKey);
                      }}
                      onDrop={() => {
                        onDrop(col);
                        setDragOverCell(null);
                        setDragActiveCol(null);
                      }}
                      onDragEnd={() => {
                        onDragEnd();
                        setDragOverCell(null);
                        setDragActiveCol(null);
                      }}
                      onMouseEnter={(e) => buildTooltip(e, pli)}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <div className="em-cell-inner">
                        <span className="em-cell-handle" title="Glisser">⠿</span>
                        <span className="em-cell-val">
                          {info?.isFace    && <span style={{ color: "#E6FF3E", marginRight: 3 }}>⭐</span>}
                          {info?.isFissure && <span style={{ color: "#ff6b6b", marginRight: 3 }}>⚠</span>}
                          {info?.isNoeud   && <span style={{ color: "#ffa040", marginRight: 3 }}>🔗</span>}
                          {pli ?? "—"}
                        </span>
                        <div className="em-cell-arrows">
                          <button
                            type="button"
                            className="em-arrow-btn"
                            onClick={() => moveCellInCol(col, rowIdx, -1)}
                            disabled={rowIdx === 0}
                            title="Monter"
                          >▲</button>
                          <button
                            type="button"
                            className="em-arrow-btn"
                            onClick={() => moveCellInCol(col, rowIdx, 1)}
                            disabled={rowIdx === recetteRows.length - 1}
                            title="Descendre"
                          >▼</button>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DensiteTooltip tooltip={tooltip} />
    </>
  );
}

/* ─────────────────────────────────────────
   SUB-COMPONENT — StackIds
───────────────────────────────────────── */
function StackIdPreview({ empCols, lastStackId, previewIds }) {
  const hasPreview = previewIds?.length > 0;
  if (!lastStackId && !hasPreview) return null;

  return (
    <div className="em-stackids-preview">
      {lastStackId && (
        <div className="em-stackids-current">
          Dernier StackId utilisé : <strong>{lastStackId}</strong>
        </div>
      )}
      {hasPreview && empCols.length > 0 && (
        <>
          <div className="em-stackids-title">Prochains StackIds (à la confirmation)</div>
          {empCols.map((col, idx) => (
            <div key={col} className="em-stackid-row em-stackid-row-preview">
              <span className="em-stackid-col">{col}</span>
              <span className="em-stackid-val em-stackid-val-preview">
                {previewIds[idx] ?? "—"}
              </span>
            </div>
          ))}
        </>
      )}
      {hasPreview && empCols.length === 0 && (
        <div className="em-stackids-current">
          Prochain StackId : <strong>{previewIds[0]}</strong>
        </div>
      )}
    </div>
  );
}

function StackIdList({ empCols, stackIds, printing, onPrint, validated }) {
  if (!stackIds.length) return null;
  return (
    <div className="em-stackids">
      <div className="em-stackids-title">
        {validated ? "🆔 StackIds validés" : "🆔 StackIds générés"}
      </div>
      {empCols.map((col, idx) => (
        <div key={col} className="em-stackid-row">
          <span className="em-stackid-col">{col}</span>
          <span className="em-stackid-val">{stackIds[idx] ?? "—"}</span>
          <button
            type="button"
            disabled={printing}
            onClick={() => onPrint(stackIds[idx])}
            className="em-btn-print"
          >
            🖨 Print
          </button>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT — Empilement v2
───────────────────────────────────────── */
export default function EmpilementV2() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();
  const now      = useClock();

  /* ── Form state ── */
  const [operator,       setOperator]       = useState("");
  const [strategie,      setStrategie]      = useState("v2-serpentin");
  const [pn1,            setPn1]            = useState("");
  const [pn2,            setPn2]            = useState("");
  const [pn3,            setPn3]            = useState("");
  const [pn4,            setPn4]            = useState("");
  const [pn5,            setPn5]            = useState("");
  const [pn6,            setPn6]            = useState("");
  const [pn7,            setPn7]            = useState("");
  const [pn8,            setPn8]            = useState("");
  const [plages,         setPlages]         = useState(EMPTY_PLAGES);

  const pnBySlot = {
    pn1: [pn1, setPn1],
    pn2: [pn2, setPn2],
    pn3: [pn3, setPn3],
    pn4: [pn4, setPn4],
    pn5: [pn5, setPn5],
    pn6: [pn6, setPn6],
    pn7: [pn7, setPn7],
    pn8: [pn8, setPn8],
  };
  const [nbEmpilements,  setNbEmpilements]  = useState("1");
  const [nbPlisRecette,  setNbPlisRecette]  = useState("28");
  const [dateRecette, setDateRecette] = useState(new Date().toISOString().split('T')[0]);
const [useDateRecette, setUseDateRecette] = useState(false);
  const [masseCible,     setMasseCible]     = useState(DEFAULT_MASSE);
  const [epaisseurCible, setEpaisseurCible] = useState(DEFAULT_EPAISSEUR);

  /* ── Dev / filtre densité ── */
  const [devMode, setDevMode] = useState(false);
  const [d1,      setD1]      = useState("");
  const [d2,      setD2]      = useState("");

  /* ── Options v2 ── */
  const [category,               setCategory]               = useState(false);
  const [effetMiroir,            setEffetMiroir]            = useState(true);
  const [maximiserStacksValides, setMaximiserStacksValides] = useState(true);
  const [filterHumidite,         setFilterHumidite]         = useState(false);
  const [seuilHumidite,          setSeuilHumidite]          = useState("1.0");

  /* ── Recette state ── */
  const [recetteColumns, setRecetteColumns] = useState([]);
  const [recetteRows,    setRecetteRows]    = useState([]);
  const [recetteStats,   setRecetteStats]   = useState([]);
  const [densitiesMap,   setDensitiesMap]   = useState({});
  const [stackIds,       setStackIds]       = useState([]);
  const [validatedCols,  setValidatedCols]  = useState([]);
  const [stackPreview,   setStackPreview]   = useState({ lastStackId: null, nextStackIds: [] });
  const [pnList,         setPnList]         = useState([]);

  /* ── UI state ── */
  const [loadingRecette, setLoadingRecette] = useState(false);
  const [sending,        setSending]        = useState(false);
  const [printing,       setPrinting]       = useState(false);
  const [error,          setError]          = useState("");
  const [success,        setSuccess]        = useState("");

  const dragHandlers = useCellDrag(setRecetteRows);

  /* ── Init ── */
  useEffect(() => {
    apiFetch("/api/tracabilite/pns")
      .then((r) => r.json())
      .then((d) => setPnList(d.pns ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) setOperator(JSON.parse(raw).username ?? "");
    } catch {}
  }, []);

  const refreshStackPreview = useCallback(async (count = 1) => {
    try {
      const r = await apiFetch(`/api/empilement/stack/preview?count=${Math.max(1, count)}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.status === "success") {
        setStackPreview({
          lastStackId: d.lastStackId ?? null,
          nextStackIds: d.nextStackIds ?? [],
        });
      }
    } catch {
      /* ignore preview errors */
    }
  }, []);

  useEffect(() => {
    refreshStackPreview(1);
  }, [refreshStackPreview]);

  /* ── Helpers ── */
  function clearRecetteTable() {
    setRecetteColumns([]);
    setRecetteRows([]);
    setRecetteStats([]);
    setDensitiesMap({});
  }

  function resetRecette() {
    clearRecetteTable();
    setStackIds([]);
    setValidatedCols([]);
  }

  function clearMessages() {
    setError("");
    setSuccess("");
  }

  /* ── Validation devMode ── */
  function validateDevMode() {
    if (!devMode) return { ok: true, d1Value: null, d2Value: null };
    if (d1 === "" || d2 === "")
      return { ok: false, msg: "Merci de renseigner d1 et d2." };
    const d1v = parseFloat(d1);
    const d2v = parseFloat(d2);
    if (Number.isNaN(d1v) || Number.isNaN(d2v))
      return { ok: false, msg: "d1 et d2 doivent être des nombres." };
    if (d1v > d2v)
      return { ok: false, msg: "d1 doit être ≤ d2." };
    return { ok: true, d1Value: d1v, d2Value: d2v };
  }

  /* ── Générer recette ── */
  async function handleGenererRecette() {
    clearMessages();

    if (!pn1.trim())  { setError("Merci de saisir au moins le PN1."); return; }


    const devCheck = validateDevMode();
    if (!devCheck.ok) { setError(devCheck.msg); return; }

    let plagesPayload;
    try {
      plagesPayload = buildPlagesPayload(plages);
    } catch (e) {
      setError(e.message);
      return;
    }

    const nb = clampInt(nbEmpilements, 1, MAX_EMPILEMENTS);
    const np = clampInt(nbPlisRecette, 1, MAX_PLIS);
    const mc = parseFloat(masseCible)     || 7000;
    const ec = parseFloat(epaisseurCible) || 28;

    try {
      setLoadingRecette(true);
      resetRecette();

      // 1. Calcul recette
      const res = await apiFetch("/api/empilement/v2/recette", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pn1:            pn1.trim(),
          pn2:            pn2.trim() || "",
          pn3:            pn3.trim() || "",
          pn4:            pn4.trim() || "",
          pn5:            pn5.trim() || "",
          pn6:            pn6.trim() || "",
          pn7:            pn7.trim() || "",
          pn8:            pn8.trim() || "",
          nbEmpilements:  nb,
          nbPlis:         np,
          date:           useDateRecette ? dateRecette : null,
          filter_date:    useDateRecette,
          masse_cible:    mc,
          epaisseur_cible: ec,
          dev:            devMode,
          d1:             devCheck.d1Value,
          d2:             devCheck.d2Value,
          category,
          effet_miroir:             effetMiroir,
          maximiser_stacks_valides: maximiserStacksValides,
          filter_humidite:          filterHumidite,
          seuil_humidite:           parseFloat(seuilHumidite) || 1.0,
          plages:                   plagesPayload,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message ?? `Erreur recette (HTTP ${res.status})`);

      const columns = data.columns ?? [];
      const empCols = columns.filter((c) => c !== "Position");

      setRecetteColumns(columns);
      setRecetteRows(data.rows ?? []);
      setRecetteStats(data.stats ?? []);
      setDensitiesMap(data.densities ?? {});

      await refreshStackPreview(empCols.length);

      setSuccess(
        `Recette générée : ${nb} empilement(s) × ${np} plis. ` +
        "Les StackIds seront attribués à la confirmation."
      );
    } catch (e) {
      setError(e.message ?? "Erreur lors de la génération de la recette.");
      resetRecette();
    } finally {
      setLoadingRecette(false);
    }
  }

  /* ── Print ── */
  async function clientPrintFromApi(bodyForApi) {
    const win = window.open("", "_blank");
    win?.document.write(`<!doctype html><title>Préparation…</title><body>Préparation…</body>`);
    win?.document.close();

    const res = await apiFetch("/api/labels/a4", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...bodyForApi, format: "PNG", download: false }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message ?? `HTTP ${res.status}`);
    }

    const blob    = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const safePN  = String(bodyForApi.pn ?? "QR").replace(/[^\w\-]+/g, "_").slice(0, 64);
    const fileName = `A4_${bodyForApi.n ?? 1}x_${safePN}.png`;
    const ANDROID  = isAndroid();

    const html = `<!doctype html><html><head>
      <meta charset="utf-8">
      <title>${fileName}</title>
      <style>
        html,body{height:100%;margin:0}
        #bar{position:fixed;inset:auto 0 0 0;background:#111;color:#fff;
          padding:8px;display:flex;gap:8px;align-items:center;z-index:10}
        #bar button,#bar a{background:#E6FF3E;color:#000;border:0;
          padding:8px 12px;font-weight:800;cursor:pointer;text-decoration:none}
        .viewer{position:fixed;left:0;right:0;top:0;bottom:44px;
          width:100%;object-fit:contain;background:#fff}
      </style>
    </head><body>
      <img class="viewer" src="${blobUrl}" alt="Étiquettes"/>
      <div id="bar">
        ${ANDROID ? "" : `<button id="btnPrint">Imprimer</button>`}
        <a href="${blobUrl}" download="${fileName}">Télécharger</a>
        <button id="btnClose">Fermer</button>
      </div>
      <script>
        function revoke(){try{URL.revokeObjectURL('${blobUrl}');}catch(e){}}
        ${ANDROID ? "" : `document.getElementById('btnPrint').onclick=()=>{window.focus();window.print();};`}
        document.getElementById('btnClose').onclick=()=>{revoke();setTimeout(()=>window.close(),100);};
        window.onafterprint=()=>{revoke();setTimeout(()=>window.close(),200);};
        ${ANDROID ? "" : `setTimeout(()=>{window.focus();window.print();},400);`}
      </script>
    </body></html>`;

    if (win) {
      win.document.open();
      win.document.write(html);
      win.document.close();
    } else {
      // Fallback — téléchargement direct si popup bloqué
      const a = document.createElement("a");
      a.href     = blobUrl;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(blobUrl);
    }
  }

  async function handlePrintStackId(sid) {
    try {
      setPrinting(true);
      await clientPrintFromApi({
        n:      2,
        pn:     sid,
        detail: "STACK Empilement",
        date:   new Date().toISOString().split("T")[0],
      });
    } catch (e) {
      setError(e.message ?? "Erreur impression Stack.");
    } finally {
      setPrinting(false);
    }
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    clearMessages();

    if (!strategie.trim())        { setError("Merci de saisir la stratégie."); return; }
    if (!recetteRows.length)      { setError("Merci de générer une recette."); return; }

    const empCols = recetteColumns.filter((c) => c !== "Position");
    const savedIds = [];
    const savedCols = [];

    try {
      setSending(true);
      for (let i = 0; i < empCols.length; i++) {
        const col  = empCols[i];
        const rows = recetteRows.map((row, idx) => ({
          position:     idx + 1,
          plyPn:        row[col] ?? "",
          commentaire:  "",
        }));

        const res = await apiFetch("/api/empilement", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            operator,
            empLabel:  col,
            strategie: strategie.trim(),
            pn1:       pn1.trim(),
            pn2:       pn2.trim() || "",
            pn3:       pn3.trim() || "",
            pn4:       pn4.trim() || "",
            pn5:       pn5.trim() || "",
            pn6:       pn6.trim() || "",
            pn7:       pn7.trim() || "",
            pn8:       pn8.trim() || "",
            nbPlis:    clampInt(nbPlisRecette, 1, MAX_PLIS),
              date: useDateRecette ? dateRecette : null,
            rows,
            timestamp: new Date().toISOString(),
          }),
        });

        const d = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(d.message ?? `Erreur sur l'empilement ${col}`);
        }
        savedIds.push(d.stackId ?? "—");
        savedCols.push(col);

        // Délai entre requêtes pour ne pas saturer l'API
        if (i < empCols.length - 1) await new Promise((r) => setTimeout(r, 800));
      }

      // Recette consommée : on vide le tableau pour empêcher une 2e confirmation
      // (doublons en base). Les StackIds restent affichés pour l'impression.
      clearRecetteTable();
      setValidatedCols(savedCols);
      setStackIds(savedIds);
      await refreshStackPreview(1);
      setSuccess(
        `✅ ${savedCols.length} empilement(s) validé(s) : ${savedIds.join(", ")}`
      );
    } catch (err) {
      if (savedCols.length) {
        // Échec partiel : on retire de la recette les stacks déjà enregistrés,
        // une nouvelle confirmation n'enverra que les restants.
        const remaining = (r) => {
          const copy = { ...r };
          savedCols.forEach((c) => delete copy[c]);
          return copy;
        };
        setRecetteColumns((cols) => cols.filter((c) => !savedCols.includes(c)));
        setRecetteRows((rows) => rows.map(remaining));
        setRecetteStats((stats) => stats.map(remaining));
        setValidatedCols(savedCols);
        setStackIds(savedIds);
        await refreshStackPreview(1);
        setError(
          `${err.message ?? "Erreur d'envoi."} — Déjà enregistrés : ${savedIds.join(", ")}. ` +
          "Ils ont été retirés de la recette : reconfirmer n'enverra que les stacks restants."
        );
      } else {
        setError(err.message ?? "Erreur d'envoi.");
      }
    } finally {
      setSending(false);
    }
  }

  /* ── Derived ── */
  const empCols = recetteColumns.filter((c) => c !== "Position");
  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });

  /* ─────────────────────────────────────── */
  return (
    <div className="em-page">
      <div className="em-card">

        {/* HEADER */}
        <div className="em-header">
          <span>Empilement v2</span>
          <span className="em-header-sub">Serpentin 28 plis — recette &amp; assignation</span>
        </div>

        {/* NAV */}
        <div className="em-nav">
          <button type="button" className="em-btn-back" onClick={() => navigate("/menu")}>
            ← Retour menu
          </button>
          <div className="em-clock">
            <div className="em-clock-time">{timeStr}</div>
            <div className="em-clock-date">{dateStr}</div>
          </div>
        </div>

        {/* OPERATEUR */}
        <div className="em-operator">
          <div className="em-operator-label">Opérateur</div>
          <div className="em-operator-value">{operator ? operator.toUpperCase() : "—"}</div>
          <div className="em-operator-dot" />
        </div>

        <form onSubmit={handleSubmit} className="em-form" noValidate>

          {/* STRATÉGIE */}
          <div className="em-section">
            <div className="em-section-label">🎯 Stratégie d'empilement</div>
            <input
              value={strategie}
              onChange={(e) => setStrategie(e.target.value)}
              className="em-input-field em-input-yellow"
              placeholder="Décrire la stratégie…"
            />
          </div>

          {/* RECETTE */}
          <div className="em-section em-section-recette">
            <div className="em-section-label">📋 Recette d'empilement</div>

            {/* PN (jusqu'à 8) + plage de plis optionnelle par lot */}
            <div className="em-grid-2">
              {PN_SLOT_KEYS.map((slot, idx) => {
                const [value, setValue] = pnBySlot[slot];
                const num = idx + 1;
                return (
                  <PnPlageInput
                    key={slot}
                    label={num === 1 ? "PN 1 *" : `PN ${num}`}
                    value={value}
                    onChange={setValue}
                    placeholder={num === 1 ? "Obligatoire" : "Optionnel"}
                    pnList={pnList}
                    plage={plages[slot]}
                    onPlageChange={(p) => setPlages((prev) => ({ ...prev, [slot]: p }))}
                  />
                );
              })}
            </div>

            {/* Nb plis / empilements */}
            <div className="em-grid-2">
              <div>
                <div className="em-input-label">Nb plis par stack *</div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={nbPlisRecette}
                  onChange={(e) => setNbPlisRecette(e.target.value.replace(/\D/g, ""))}
                  placeholder={`1 — ${MAX_PLIS}`}
                  className="em-input-field em-input-big-num"
                />
              </div>
              <div>
                <div className="em-input-label">Nb empilements *</div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={nbEmpilements}
                  onChange={(e) => setNbEmpilements(e.target.value.replace(/\D/g, ""))}
                  placeholder={`1 — ${MAX_EMPILEMENTS}`}
                  className="em-input-field em-input-big-num"
                />
              </div>
            </div>

            {/* Date fabrication */}
           <div>
  <label className="em-dev-toggle">
    <input
      type="checkbox"
      checked={useDateRecette}
      onChange={e => setUseDateRecette(e.target.checked)}
    />
    <span>Spécifier une date de fabrication</span>
  </label>
  {useDateRecette && (
    <input
      type="date"
      value={dateRecette}
      onChange={e => setDateRecette(e.target.value)}
      className="em-input-field em-input-date"
      style={{ marginTop: 8 }}
    />
  )}
</div>

            {/* Cibles qualité */}
           {/* <div className="em-section em-section-nested">
              <div className="em-section-label" style={{ color: "#7ab8cc" }}>⚖ Cibles qualité</div>
              <div className="em-grid-2">
                <div>
                  <div className="em-input-label">Masse cible (g)</div>
                  <input
                    type="number"
                    step="100"
                    value={masseCible}
                    onChange={(e) => setMasseCible(e.target.value)}
                    placeholder={DEFAULT_MASSE}
                    className="em-input-field em-input-big-num"
                  />
                  <div className="em-input-hint">Tolérance ±200 g</div>
                </div>
                <div>
                  <div className="em-input-label">Épaisseur cible (mm)</div>
                  <input
                    type="number"
                    step="0.5"
                    value={epaisseurCible}
                    onChange={(e) => setEpaisseurCible(e.target.value)}
                    placeholder={DEFAULT_EPAISSEUR}
                    className="em-input-field em-input-big-num"
                  />
                  <div className="em-input-hint">Tolérance ±1 mm</div>
                </div>
              </div>
            </div>*/}

            {/* Options v2 */}
            <div className="em-section em-section-nested">
              <div className="em-section-label" style={{ color: "#7ab8cc" }}>⚙ Options v2</div>
              <label className="em-dev-toggle">
                <input
                  type="checkbox"
                  checked={category}
                  onChange={(e) => setCategory(e.target.checked)}
                />
                <span>Mode catégorie (HIGH/MEDIUM/LOW + pattern)</span>
              </label>
              <label className="em-dev-toggle">
                <input
                  type="checkbox"
                  checked={effetMiroir}
                  disabled={category}
                  onChange={(e) => setEffetMiroir(e.target.checked)}
                />
                <span>Effet miroir (profil en V, serpentin uniquement)</span>
              </label>
              <label className="em-dev-toggle">
                <input
                  type="checkbox"
                  checked={maximiserStacksValides}
                  onChange={(e) => setMaximiserStacksValides(e.target.checked)}
                />
                <span>Maximiser les stacks valides</span>
              </label>
              <label className="em-dev-toggle">
                <input
                  type="checkbox"
                  checked={filterHumidite}
                  onChange={(e) => setFilterHumidite(e.target.checked)}
                />
                <span>Filtrer par humidité</span>
              </label>
              {filterHumidite && (
                <div style={{ marginTop: 8 }}>
                  <div className="em-input-label">Seuil humidité max (%)</div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={seuilHumidite}
                    onChange={(e) => setSeuilHumidite(e.target.value)}
                    className="em-input-field em-input-big-num"
                  />
                </div>
              )}
            </div>

            {/* Filtre densité */}
            <div className="em-section em-section-nested">
              <div className="em-section-label" style={{ color: "#7ab8cc" }}>🧪 Filtre densité</div>
              <label className="em-dev-toggle">
                <input
                  type="checkbox"
                  checked={devMode}
                  onChange={(e) => {
                    setDevMode(e.target.checked);
                    if (!e.target.checked) { setD1(""); setD2(""); }
                  }}
                />
                <span>Activer le filtrage par densité</span>
              </label>
              {devMode && (
                <div className="em-grid-2" style={{ marginTop: 8 }}>
                  <div>
                    <div className="em-input-label">Densité min (d1)</div>
                    <input
                      type="number"
                      step="0.0001"
                      value={d1}
                      onChange={(e) => setD1(e.target.value)}
                      placeholder="ex: 0.4200"
                      className="em-input-field em-input-big-num"
                    />
                  </div>
                  <div>
                    <div className="em-input-label">Densité max (d2)</div>
                    <input
                      type="number"
                      step="0.0001"
                      value={d2}
                      onChange={(e) => setD2(e.target.value)}
                      placeholder="ex: 0.6800"
                      className="em-input-field em-input-big-num"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bouton générer */}
            <button
              type="button"
              onClick={handleGenererRecette}
              disabled={loadingRecette}
              className="em-btn-generer"
            >
              {loadingRecette ? "⏳ Calcul en cours…" : "⚡ Générer la recette"}
            </button>

            {/* TABLEAU + STATS */}
            {recetteRows.length > 0 && (
              <>
                <RecetteTable
                  empCols={empCols}
                  recetteRows={recetteRows}
                  setRecetteRows={setRecetteRows}
                  densitiesMap={densitiesMap}
                  dragHandlers={dragHandlers}
                />
                <StatsTable stats={recetteStats} empCols={empCols} />
              </>
            )}

            {/* STACK IDS — prévisualisation ou validés */}
            {!stackIds.length && (
              <StackIdPreview
                empCols={empCols}
                lastStackId={stackPreview.lastStackId}
                previewIds={stackPreview.nextStackIds}
              />
            )}
            <StackIdList
              empCols={validatedCols}
              stackIds={stackIds}
              printing={printing}
              onPrint={handlePrintStackId}
              validated={stackIds.length > 0}
            />
          </div>

          {/* Messages */}
          {error   && <div className="em-msg em-msg-error">⚠ {error}</div>}
          {success && <div className="em-msg em-msg-success">{success}</div>}

          {/* Submit */}
          <div className="em-submit-row">
            <button
              type="submit"
              disabled={sending || !recetteRows.length}
              className="em-btn-submit"
            >
              {sending ? "⏳ Envoi en cours…" : "✅ Confirmation Stack"}
            </button>
            <div className="em-timebox">
              <div className="em-timebox-label">TIME</div>
              <div className="em-timebox-value">{timeStr}</div>
            </div>
          </div>

        </form>
      </div>
      <Style />
    </div>
  );
}

/* ── STYLES ── */
function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .em-page {
        min-height: 100vh; width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        display: flex; justify-content: center; align-items: flex-start;
        padding: 16px; font-family: 'Inter','Segoe UI',sans-serif;
      }
      .em-card {
        width: 100%; max-width: 720px;
        background: #011e2b; border: 1px solid #1a4a5a;
        border-radius: 16px; overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px #0a3040;
      }
      .em-header {
        background: linear-gradient(135deg,#E6FF3E 0%,#c8e800 100%);
        color: #000; padding: 14px 20px;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
      }
      .em-header span  { font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
      .em-header-sub   { font-size: 11px; font-weight: 500; opacity: 0.6; }
      .em-nav {
        background: #010f14; padding: 8px 16px;
        display: flex; justify-content: space-between; align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }
      .em-btn-back {
        background: transparent; color: #7ab8cc; border: 1px solid #1a4a5a;
        border-radius: 6px; padding: 5px 10px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s;
      }
      .em-btn-back:hover { background: #1a4a5a; color: #fff; }
      .em-clock          { text-align: right; }
      .em-clock-time     { color: #E6FF3E; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
      .em-clock-date     { color: #5a8a99; font-size: 10px; }
      .em-operator {
        margin: 14px 20px;
        background: linear-gradient(135deg, #001f2e, #012836);
        border: 2px solid #E6FF3E; border-radius: 14px; padding: 16px 24px;
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        position: relative; overflow: hidden;
      }
      .em-operator::before {
        content: ""; position: absolute; inset: 0;
        background: radial-gradient(ellipse at top, rgba(230,255,62,0.05) 0%, transparent 70%);
      }
      .em-operator-label { color: #E6FF3E; font-size: 11px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; }
      .em-operator-value { color: #fff; font-size: 32px; font-weight: 900; letter-spacing: 0.06em; text-align: center; }
      .em-operator-dot   { width: 10px; height: 10px; background: #00B050; border-radius: 50%; box-shadow: 0 0 8px #00B050; }

      .em-form { padding: 0 20px 24px; display: flex; flex-direction: column; gap: 18px; }
      .em-section {
        background: #012030; border: 1px solid #1a4a5a;
        border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px;
      }
      .em-section-recette { background: #011929; border-color: #E6FF3E; }
      .em-section-label   { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #E6FF3E; }
      .em-input-label { color: #E6FF3E; font-size: 11px; font-weight: 700; margin-bottom: 5px; letter-spacing: 0.05em; }
      .em-input-field {
        width: 100%; background: #003347; color: #fff;
        border: 1px solid #E6FF3E; border-radius: 8px;
        padding: 10px 12px; font-size: 13px; font-weight: 600;
        outline: none; transition: border 0.2s, box-shadow 0.2s; font-family: inherit;
      }
      .em-input-field:focus  { border-color: #fff; box-shadow: 0 0 0 2px rgba(230,255,62,0.2); }
      .em-input-yellow       { border-color: #FFD966; background: #012836; font-size: 15px; }
      .em-input-yellow:focus { border-color: #FFD966; box-shadow: 0 0 0 2px rgba(255,217,102,0.2); }
      .em-input-big-num      { text-align: center; font-size: 20px; font-weight: 800; padding: 12px; }
      .em-input-date         { font-size: 14px; font-weight: 700; text-align: center; cursor: pointer; color-scheme: dark; }
      .em-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
      .em-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .em-pn-plage { display: flex; flex-direction: column; gap: 6px; }
      .em-plage-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .em-pn-dropdown {
        position: absolute; top: 100%; left: 0; right: 0;
        background: #011d29; border: 2px solid #E6FF3E;
        border-radius: 0 0 8px 8px; max-height: 220px; overflow-y: auto; z-index: 999;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .em-pn-dropdown-item {
        padding: 7px 12px; cursor: pointer; color: #fff; font-size: 11px;
        border-bottom: 1px solid #013d52; transition: background 0.15s;
      }
      .em-pn-dropdown-item:hover { background: #013d52; }
      .em-btn-generer {
        background: linear-gradient(135deg,#E6FF3E,#c8e800); color: #000;
        border: none; border-radius: 10px; padding: 14px;
        font-weight: 900; font-size: 15px; cursor: pointer; width: 100%;
        letter-spacing: 0.04em; transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(230,255,62,0.2);
      }
      .em-btn-generer:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .em-btn-generer:disabled             { opacity: 0.5; cursor: not-allowed; }

      /* ── HINT ── */
      .em-reorder-hint {
        display: flex; align-items: center; gap: 8px;
        background: #0a2030; border: 1px dashed #E6FF3E;
        border-radius: 8px; padding: 8px 14px;
        color: #E6FF3E; font-size: 11px; letter-spacing: 0.03em; user-select: none;
      }
      .em-reorder-hint span { font-size: 16px; }
      .em-reorder-hint strong { font-weight: 800; }

      /* ── TABLEAU ── */
      .em-table-wrap { overflow-x: auto; border-radius: 8px; border: 1px solid #1a4a5a; }
      .em-table      { border-collapse: collapse; width: 100%; font-size: 11px; min-width: 320px; }

      /* en-têtes */
      .em-th { padding: 8px 6px; background: #012a3d; color: #E6FF3E; font-weight: 700; font-size: 11px; text-align: center; border: 1px solid #1a4a5a; white-space: nowrap; }
      .em-th-pos    { width: 40px; color: #5a8a99; }
      .em-th-active { background: #1a2800 !important; border-bottom: 2px solid #E6FF3E !important; }
      .em-th-inner  { display: flex; align-items: center; justify-content: center; gap: 6px; }

      /* bouton reset par colonne (dans le th) */
      .em-btn-col-reset {
        background: transparent; color: #5a8a99; border: none;
        font-size: 13px; cursor: pointer; padding: 0; line-height: 1;
        transition: color 0.15s;
      }
      .em-btn-col-reset:hover { color: #E6FF3E; }

      /* lignes */
      .em-tr-even  { background: #011820; }
      .em-tr-odd   { background: #012030; }

      /* cellules générales */
      .em-td       { border: 1px solid #1a3a4a; vertical-align: middle; padding: 0; }
      .em-td-pos   { text-align: center; color: #E6FF3E; font-weight: 800; font-size: 12px; padding: 6px 4px; font-variant-numeric: tabular-nums; }

      /* cellule draggable */
      .em-td-cell       { cursor: grab; transition: background 0.15s; }
      .em-td-cell:active { cursor: grabbing; }
      .em-td-col-active  { background: rgba(230,255,62,0.04) !important; }
      .em-td-dragover    { outline: 2px solid #E6FF3E !important; outline-offset: -2px; background: #1a2800 !important; }

      /* contenu interne d'une cellule */
      .em-cell-inner {
        display: flex; align-items: center; gap: 4px;
        padding: 4px 6px; min-height: 34px;
      }
      .em-cell-handle {
        color: #2a5a6a; font-size: 14px; user-select: none; flex-shrink: 0;
        letter-spacing: -1px; cursor: grab;
      }
      .em-cell-val {
        flex: 1; color: #c8d8e8; font-size: 11px;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        text-align: left; min-width: 0;
      }
      .em-cell-arrows { display: flex; flex-direction: column; gap: 1px; flex-shrink: 0; }
      .em-arrow-btn {
        background: #012a3d; color: #E6FF3E;
        border: 1px solid #1a4a5a; border-radius: 3px;
        width: 18px; height: 14px; font-size: 8px; font-weight: 800;
        cursor: pointer; line-height: 1; padding: 0;
        transition: background 0.15s; display: flex; align-items: center; justify-content: center;
      }
      .em-arrow-btn:hover:not(:disabled) { background: #1a4a5a; }
      .em-arrow-btn:disabled             { opacity: 0.2; cursor: not-allowed; }

      /* stats footer */
      .em-td-stat { background: #001f2e; color: #E6FF3E; font-weight: 700; font-size: 10px; padding: 6px; border: 1px solid #1a4a5a; text-align: center; }

      /* STACK IDS */
      .em-stackids       { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
      .em-stackids-title { color: #E6FF3E; font-size: 12px; font-weight: 800; letter-spacing: 0.06em; }
      .em-stackids-preview { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; padding: 12px; border: 1px dashed #1a4a5a; border-radius: 10px; background: rgba(255,255,255,0.02); }
      .em-stackids-current  { color: #94a3b8; font-size: 12px; }
      .em-stackids-current strong { color: #E6FF3E; font-family: monospace; }
      .em-stackid-val-preview { color: #7ab8cc !important; font-style: italic; }
      .em-stackids-hint  { color: #94a3b8; font-size: 12px; font-style: italic; margin-top: 8px; }
      .em-stackid-row    { display: flex; justify-content: space-between; align-items: center; gap: 10px; background: #001f2e; border: 1px solid #E6FF3E; border-radius: 8px; padding: 10px 14px; }
      .em-stackid-col    { color: #E6FF3E; font-size: 11px; font-weight: 800; min-width: 52px; }
      .em-stackid-val    { color: #fff; font-size: 12px; font-family: monospace; letter-spacing: 0.05em; flex: 1; }
      .em-btn-print      { background: #FFD966; color: #000; border: 2px solid #C00000; border-radius: 6px; padding: 6px 12px; font-weight: 800; font-size: 11px; cursor: pointer; white-space: nowrap; transition: opacity 0.2s; }
      .em-btn-print:disabled            { opacity: 0.5; cursor: not-allowed; }
      .em-btn-print:hover:not(:disabled){ opacity: 0.85; }

      /* MESSAGES */
      .em-msg         { border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
      .em-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .em-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* SUBMIT */
      .em-submit-row { display: flex; gap: 10px; align-items: stretch; }
      .em-btn-submit {
        flex: 1; background: linear-gradient(135deg,#00B050,#008a3c);
        color: #000; border: none; border-radius: 10px; padding: 16px;
        font-size: 16px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.08em;
        cursor: pointer; transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .em-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .em-btn-submit:disabled             { opacity: 0.5; cursor: not-allowed; }
      .em-timebox {
        background: #C00000; color: #fff; border: 2px solid #7F0000;
        border-radius: 8px; padding: 10px 16px; text-align: center;
        min-width: 120px; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 2px;
      }
      .em-timebox-label { font-size: 11px; font-weight: 800; letter-spacing: 0.15em; }
      .em-timebox-value { font-size: 18px; font-weight: 800; font-variant-numeric: tabular-nums; }

      @media (min-width: 768px) {
        .em-card { max-width: 860px; }
        .em-cell-val { font-size: 12px; }
        .em-arrow-btn { width: 20px; height: 16px; font-size: 9px; }
        .em-th, .em-td-stat { font-size: 12px; }
      }
      @media (min-width: 1024px) {
        .em-card { max-width: 1040px; }
        .em-form { padding: 0 36px 36px; }
        .em-cell-val   { font-size: 13px; }
        .em-cell-inner { padding: 5px 8px; min-height: 38px; }
        .em-arrow-btn  { width: 22px; height: 18px; font-size: 10px; }
        .em-cell-handle { font-size: 16px; }
        .em-th { font-size: 13px; padding: 10px 8px; }
      }
      @media (min-width: 1400px) {
        .em-page  { padding: 32px; }
        .em-card  { max-width: 1280px; border-radius: 20px; }
        .em-header span { font-size: 28px; }
        .em-form  { padding: 0 40px 40px; gap: 28px; }
        .em-operator-value { font-size: 52px; }
        .em-cell-val    { font-size: 14px; }
        .em-cell-inner  { padding: 7px 10px; min-height: 44px; gap: 6px; }
        .em-arrow-btn   { width: 26px; height: 20px; font-size: 11px; border-radius: 4px; }
        .em-cell-handle { font-size: 18px; }
        .em-th  { font-size: 14px; padding: 12px 10px; }
        .em-btn-submit  { font-size: 22px; padding: 24px; }
        .em-timebox-value { font-size: 28px; }
        .em-dev-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
}

.em-dev-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #E6FF3E;
  cursor: pointer;
}

      }
    `}</style>
  );
}
